import { withFieldGroup } from '@massilia/features'
import { cn } from '@massilia/ui'
import { AlertTriangle, Mail, Phone, User } from 'lucide-react'

import type { ReservationSchema } from '@/lib/schemas/reservation/reservation.schema'

import { SectionWrapper } from '../-components/section-wrapper'

interface ContactDetailsFormGroupProps {
  client: {
    email: null | string
    firstName: string
    lastName: string
    phoneNumbers: { number: string; type: string }[]
  }
}

export const ContactDetailsFormGroup = withFieldGroup({
  defaultValues: {} as Pick<
    ReservationSchema,
    'complementaryInformation' | 'otherContactPhone' | 'otherEmailAddress'
  >,
  props: {} as ContactDetailsFormGroupProps,
  render: function ContactDetails({ client, group }) {
    return (
      <SectionWrapper
        icon={User}
        title="Informations de contact"
        description="Vos coordonnées pour ce voyage"
      >
        <div className="space-y-6">
          {/* Client info display (read-only) */}
          <div className="rounded-xl border border-stone-200 bg-stone-50/50 p-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1">
                <span className="text-xs font-medium tracking-wider text-stone-500 uppercase">
                  Nom complet
                </span>
                <p className="flex items-center gap-2 text-sm font-medium text-stone-800">
                  <User className="h-4 w-4 text-stone-400" />
                  {client.firstName} {client.lastName}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-xs font-medium tracking-wider text-stone-500 uppercase">
                  Email
                </span>
                <p className="flex items-center gap-2 text-sm font-medium text-stone-800">
                  <Mail className="h-4 w-4 text-stone-400" />
                  {client.email ?? 'Non renseigné'}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-xs font-medium tracking-wider text-stone-500 uppercase">
                  Téléphone
                </span>
                <p className="flex items-center gap-2 text-sm font-medium text-stone-800">
                  <Phone className="h-4 w-4 text-stone-400" />
                  {client.phoneNumbers?.find((p) => p.type === 'PRINCIPAL')?.number ??
                    client.phoneNumbers?.[0]?.number ??
                    ''}
                </p>
              </div>
            </div>
          </div>

          {/* Email warning */}
          <div
            className={cn(
              'flex items-start gap-3 rounded-xl p-4',
              'border border-amber-200 bg-linear-to-br from-amber-50 to-orange-50/50'
            )}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">Important pour la réception</p>
              <p className="mt-1 text-xs leading-relaxed text-amber-700">
                Privilégiez les adresses <strong>GMAIL</strong>, <strong>HOTMAIL</strong>,{' '}
                <strong>OUTLOOK</strong> ou <strong>LIVE</strong>.
                <br />
                Évitez FREE, SFR, LA POSTE ou ORANGE pour garantir la réception de vos documents.
              </p>
            </div>
          </div>

          {/* Optional contact fields */}
          <div className="grid gap-4 sm:grid-cols-2">
            <group.AppField
              name="otherEmailAddress"
              children={(field) => (
                <field.TextField
                  icon={Mail}
                  label="Autre adresse email"
                  placeholder="autre@email.com"
                  optional
                />
              )}
            />
            <group.AppField
              name="otherContactPhone"
              children={(field) => (
                <field.TextField
                  icon={Phone}
                  label="Autre numéro de téléphone"
                  placeholder="06 12 34 56 78"
                  optional
                />
              )}
            />
          </div>

          {/* Complementary information */}
          <group.AppField
            name="complementaryInformation"
            children={(field) => (
              <field.TextAreaField
                label="Informations complémentaires"
                placeholder="Demandes spéciales, régimes alimentaires, assistance mobilité..."
                optional
              />
            )}
          />
        </div>
      </SectionWrapper>
    )
  },
})
